<template>
  <div>
    <loading-overlay :loading="histogramIsLoading" />
    <div ref="barContainer" />
  </div>
</template>

<script>
const d3 = require("d3v6");
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import Helper from "@/vue-d3-component-wrappers/common-lib/Helper.js";
import expression from '../store/protein/modules/expression';
export default {
  name: "Histogram",
  components: {
    LoadingOverlay
  },
  props: {
    binNumber: { //can be changed for user experience
      type: Number,
      default: 30,
    },
    width: {
      type: Number,
      default: 883,
    },
    height: {
      type: Number,
      default: 460,
    },
    marginBottom: {
      type: Number,
      default: 30,
    },
    marginLeft: {
      type: Number,
      default: 100,
    },
    marginRight: {
      type: Number,
      default: 20,
    },
    marginTop: {
      type: Number,
      default: 20,
    },
    /* following props are needed for the CaclutationView which fetches the data */
    tissues_selected: {
      type: String,
      default: '',
    },
    tissue_category: {
      type: String,
      default: '',
    },
    quantification: {
      type: String,
      default: '',
    },
    unit: {
      type: String,
      default: '',
    },
    scope: {
      type: Number,
      default: 1,
    }
  },
  data: () => ({
    histogramIsLoading: false,
    dataIn: []
  }),
  computed: {
    ...mapState({
      organismTaxcode: state => state.organismTaxcode
    }),
    ...mapGetters({
      proteinInfo: 'protein/getProteinInfo',
      proteinId: 'protein/getProteinId',
      hasData: 'protein/proteinSummary/hasData',
      summaryData: 'protein/proteinSummary/getData',
      omicsTypes: 'protein/expression/getOmicsTypes',
      selection: 'protein/expression/getSelection',
      loadedSelection: 'protein/expression/getLoadedSelection',
    })
  },
  mounted() {
    // reloads the histogram if the user goes back to the summary page
    if(this.proteinInfo.database != null && this.proteinInfo.database != '' && !this.histogramIsLoading){
      this.fetchBinData();
    }
  },
  watch: {
    histogramIsLoading (){
      if(!this.histogramIsLoading){
        console.log('expression histogram finished loading');
        this.drawChart();
      }else{
        console.log('expression histogram is loading...');
      }
    },
    proteinInfo () {
      if(!this.histogramIsLoading){
        this.fetchBinData();
      }
    },
    // reloads histogram if the data selection changes
    loadedSelection(){
      if(!this.histogramIsLoading){
        this.fetchBinData();
      }
    },
  },
  methods: {
    async fetchBinData () {
      this.histogramIsLoading = true;

      const response = await axios.get(
        `${this.$store.state.host}/proteomicsdb/logic/getExpressionWrapperForTaxcode.xsjs`,
        {
          params: {
              database: this.proteinInfo.database,
              taxcode: this.organismTaxcode,
              tissues_selected: this.tissues_selected,
              tissue_category: this.selection.bioSources.toString().replaceAll(',', ';'),
              omics: this.selection.omicsType,
              quantification: this.selection.quantification.method,
              unit: this.selection.quantification.unit,
              scope: this.scope
          }
        }
      )

      this.dataIn = response.data

      this.histogramIsLoading = false;
      this.$emit("histogramIsLoading", false); 
    },
    drawChart() {
      d3.select(this.$refs.barContainer).select("svg").remove(); // Add this line to clear any previous SVGs
      // Declare the chart dimensions and margins.
      const width = this.width;
      const height = this.height;
      const marginTop = this.marginTop;
      const marginRight = this.marginRight;
      const marginBottom = this.marginBottom;
      const marginLeft = this.marginLeft;
      const binNumber = this.binNumber;
      let binIndexOfInputProteinId = null;
      let expressionOfInputProteinId = null;

      /******** This is the bin calculation in the frontend **********/
       let binWidthTmp = d3.max(this.dataIn, (d) => d.EXPRESSION) / binNumber;

       // necessary so that the last data point is also included in the last bin, otherise one data point is always missing in the histogram
       const binWidth = (Math.round(binWidthTmp * 10 ** 3) / 10 ** 3) + 0.001; 

       const bins = Array.from({ length: binNumber}, () => ({
         x0: 0,
         x1: 0,
         length: 0, // gets updated later on
       }));

       // Fill bins array
       bins.forEach((bin, i) => {
         bin.x0 = (i === 0) ? 0 : bins[i - 1].x1;
         bin.x1 = bin.x0 + binWidth;
       });

       this.dataIn.forEach((d) => {
       for (let i = 0; i < binNumber; i++) {
         const bin = bins[i];
         if (d.EXPRESSION >= bin.x0 && d.EXPRESSION < bin.x1) {
           bin.length += 1;
           if(this.proteinId != null && d.PROTEIN_ID == this.proteinId){
             binIndexOfInputProteinId = i; // bin at position i needs to be in a different color
             expressionOfInputProteinId = d.EXPRESSION;
           }
           break;
         }
       }
       });

      // Declare the x (horizontal position) scale.
      const x = d3.scaleLinear()
          .domain([0, bins[bins.length - 1].x1])
          .range([marginLeft, width - marginRight]);

      // Declare the y (vertical position) scale.
      const y = d3.scaleLinear()
          .domain([0, d3.max(bins, (d) => d.length)])
          .range([height - marginBottom, marginTop + 15]);

      // Create the SVG container.
      const svg = d3.select(this.$refs.barContainer)
          .append("svg")
          .attr("width", width)
          .attr("height", height)
          .attr("viewBox", [0, 0, width, height + 28])
          .attr("style", "max-width: 100%; height: auto;");

      // Calculate the bin midpoints
      const binMidpoints = bins.map((bin) => (bin.x0 + bin.x1) / 2);

      const tooltip = d3.select("body")
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("background-color", "white")
        .style("padding", "5px")
        .style("border", "2px solid black")
        .style("border-radius", "5px")
        .style("font-size", "14px")
        .style("font-family", "Roboto, sans-serif")
        .style("visibility", "hidden");

      // Add a rect for each bin.
      svg.append("g")   
        .selectAll()
        .data(bins)
        .join("rect")
          .attr("x", (d) => x(d.x0) + 1)
          .attr("width", (d) => x(d.x1) - x(d.x0) -1)
          .attr("y", (d) => y(Math.max(d.length, 0.05)))
          .attr("height", (d) => (y(0) - y(d.length)))
          .attr("fill", (d, i) => (i == binIndexOfInputProteinId && binIndexOfInputProteinId != null) ? "rgb(0, 194, 249)" : "rgb(0, 72, 158)")
          .on("mouseover", function(event, d) {
        d3.select(this)
          .attr("stroke", "black")
          .attr("stroke-width", 3);
        tooltip.style("visibility", "visible")
          .html(`Bin width: [${d.x0.toFixed(3)}, ${d.x1.toFixed(3)})<br>Frequency: ${d.length}`);
          })
          .on("mousemove", function(event) {
        tooltip.style("top", (event.pageY - 180) + "px")
          .style("left", (event.pageX - 80) + "px");
          })
          .on("mouseout", function() {
        d3.select(this)
          .attr("stroke", "none");
        tooltip.style("visibility", "hidden");
          });

      // Add text label for the input protein expression
      if (binIndexOfInputProteinId !== null) {
        svg.append("text")
          .attr("x", (width - marginLeft - marginRight) / 2 + marginLeft)
          .attr("y", marginTop)
          .attr("text-anchor", "middle")
          .attr("fill", "black")
          .attr("font-size", "16px")
          .attr("font-family", "Roboto, sans-serif")
            .html(`Expression of ${this.proteinInfo.name}: <tspan font-weight="bold">${Number(expressionOfInputProteinId).toFixed(3)}</tspan>`)
          .on("mousemove", function(event) {
            tooltip.style("top", (event.pageY - 180) + "px")
              .style("left", (event.pageX - 80) + "px");
          })
          .on("mouseout", function() {
            d3.select(this)
              .attr("stroke", "none");
            tooltip.style("visibility", "hidden");
          });
      }

      // Add the x-axis and label.
      svg.append("g")
          .attr("transform", `translate(0,${height - marginBottom})`)
          .call(d3.axisBottom(x)
              .tickValues([
                ...((this.binNumber < 10) ? binMidpoints : []),
                ...((this.binNumber <= 20) ? bins.map((bin, i) => (bin.x1)) : []),
                ...((this.binNumber > 20 && this.binNumber <= 30) ? bins.map((bin, i) => (i % 2 === 0 ? bin.x1 : "")) : []),
                ...((this.binNumber > 30 && this.binNumber <= 80) ? bins.map((bin, i) => (i % 5 === 0 ? bin.x1 : "")) : []),
                ...((this.binNumber > 80) ? bins.map((bin, i) => (i % 8 === 0 ? bin.x1 : "")) : [])
              ])
              .tickSizeOuter(0)
              .tickFormat((d) => (d != "") ? d.toFixed(1) : ""))
          .call((g) => g.append("text")
              .attr("x", width - 300)
              .attr("y", marginBottom + 20)
              .attr("fill", "currentColor")
              .attr("text-anchor", "end")
              .text("Protein Expression"))
          .selectAll("text")
          .attr("style", `font-family: Roboto, sans-serif`)
          .attr("style", `font-size: 18px`);

      // Add the y-axis and label, and remove the domain line.
      svg.append("g")
          .attr("transform", `translate(${marginLeft},0)`)
           .call(d3.axisLeft(y).ticks(10))
          // removes the line of the axis: .call((g) => g.select(".domain").remove())
          .call((g) => g.append("text")
              .attr("x", marginLeft - 370)
              .attr("y", marginTop - 70)
              .attr("fill", "currentColor")
              .attr("transform", "rotate(-90)")
              .attr("text-anchor", "start")
              .text("Frequency"))
              .selectAll("text")
              .attr("style", `font-family: Roboto, sans-serif`)
              .attr("style", `font-size: 18px`);

      //adds line over bars
       const line = d3.line()
      .x((d, i) => x(binMidpoints[i]))
      .y((d) => y(d.length));

      svg.append("path")
          .datum(bins)
          .attr("fill", "none")
          .attr("stroke", "rgb(0, 121, 250)")
          .attr("stroke-width", 3)
          .attr("d", line);
 
      return svg.node();
    },
    getSVG: function () {
      return d3.select(this.$refs.barContainer).selectAll("svg").node();
    },
    getCSV: function () {
      var aColumnMapping = [];
      var title = "";
        aColumnMapping = [
          {
            jsonProperty: "PROTEIN_ID",
            columnName: "Protein ID",
          },
          {
            jsonProperty: "EXPERIMENT_ID",
            columnName: "Experiment ID",
          },
          {
            jsonProperty: "UNNORMALIZED_INTENSITY",
            columnName: "Unnormalized Intensity",
          },
          {
            jsonProperty: "EXPRESSION",
            columnName: "Expression",
          },
          {
            jsonProperty: "MAX_EXPRESSION",
            columnName: "Max Expression",
          },
          {
            jsonProperty: "MIN_EXPRESSION",
            columnName: "Min Expression",
          },
          {
            jsonProperty: "NUMBER_OF_SAMPLES",
            columnName: "Number of Samples",
          },
          {
            jsonProperty: "METHOD",
            columnName: "Method",
          },
          {
            jsonProperty: "UNIT",
            columnName: "Unit",
          },
          {
            jsonProperty: "ORGANISM",
            columnName: "Organism",
          },
        ];
        title = "Protein Expression: " + this.proteinInfo.uniprotId;

        return Helper.downloadCsvFromJson(
          this.dataIn,
          title,
          aColumnMapping
        );
    }
  }
};
</script>

<style scoped>
</style>

